/* winFx — le spectacle du ticket gagnant : une salve de confettis, puis un
   feu d'artifice.

   Tout le fichier tient sur deux exigences.

   1. RIEN DE TOUT ÇA NE DOIT APPARAÎTRE SUR UNE CAPTURE. Le ticket gagnant dit
      « Conservez cet écran » : le passager le photographie pour prouver son
      lot, et l'application sait aussi l'exporter en PNG. Une particule figée en
      travers du nom du lot n'est pas un souvenir, c'est un défaut. D'où deux
      garde-fous, l'un structurel, l'autre temporel :

        — le spectacle se joue sur SON PROPRE canvas rattaché à <body>, donc
          hors du sous-arbre du ticket. `toPng` ne clone que le nœud qu'on lui
          passe : les particules lui sont structurellement inatteignables, sans
          qu'on ait à les filtrer ;
        — il s'arrête vite et se RETIRE DU DOM — de lui-même au bout de
          FX_TOTAL_MS, au premier geste du passager, et sur demande avant une
          capture. Un canvas retiré ne peut plus salir aucune photo, y compris
          celle que le téléphone prend sans nous prévenir.

   2. Il s'arrête TOUJOURS. `stop()` est idempotent et coupe tout : minuteurs,
      cadence des fusées, particules en vol, écouteur, nœud.
*/
import confetti from "canvas-confetti";

// Au-dessus du ticket plein écran (z-50 dans ScratchDialog), jamais dans le
// chemin du tactile : le passager continue de faire défiler au travers.
const FX_Z_INDEX = 60;

// Palette de repli, reprise des couleurs du ticket lui-même (bleu du message,
// verts du gain, or du vernis) pour que le spectacle reste dans la charte
// quand l'annonceur n'a pas de teinte exploitable.
const FALLBACK_COLORS = ["#17BE1A", "#1FAA59", "#2438E8", "#FFC247", "#FFFFFF"];

// Le feu d'artifice démarre quand les confettis d'ouverture retombent : les
// deux effets se succèdent au lieu de se superposer en bouillie.
const FIREWORKS_DELAY_MS = 500;
const FIREWORKS_DURATION_MS = 2600;
const SHELL_INTERVAL_MS = 340;
// De quoi laisser la dernière fusée retomber avant d'effacer.
const FALLOUT_MS = 1400;
// Le grattage se termine souvent par un dernier `pointerdown` parasite. On
// n'arme l'échappatoire au toucher qu'une fois la main franchement levée,
// sinon le spectacle se coupe dans la seconde qui suit la révélation.
const TAP_SKIP_ARM_MS = 700;

// Durée totale, rideau compris : au-delà, l'écran est de nouveau nu.
export const FX_TOTAL_MS =
  FIREWORKS_DELAY_MS + FIREWORKS_DURATION_MS + FALLOUT_MS;

const rand = (min, max) => Math.random() * (max - min) + min;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Lance le spectacle et rend la fonction qui l'arrête. L'appelant DOIT la
   garder : c'est elle qu'on déclenche avant une capture et au démontage. */
export function runWinFx({ colors = [] } = {}) {
  const noop = () => {};
  if (typeof document === "undefined" || prefersReducedMotion()) return noop;

  const palette = [
    ...new Set([...colors.filter(Boolean), ...FALLBACK_COLORS]),
  ].slice(0, 6);

  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  canvas.dataset.testid = "win-fx";
  Object.assign(canvas.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: String(FX_Z_INDEX),
  });
  document.body.appendChild(canvas);

  /* Pas de `useWorker` : le rendu partirait dans un OffscreenCanvas, qui peut
     encore peindre une image après l'arrêt. Ici la coupure doit être franche et
     synchrone — c'est elle qui garantit un écran propre au moment de la photo. */
  const fire = confetti.create(canvas, { resize: true });

  let stopped = false;
  let shells = null;
  const timers = new Set();

  const later = (fn, ms) => {
    const id = setTimeout(() => {
      timers.delete(id);
      if (!stopped) fn();
    }, ms);
    timers.add(id);
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    timers.forEach(clearTimeout);
    timers.clear();
    if (shells) clearInterval(shells);
    shells = null;
    document.removeEventListener("pointerdown", stop);
    try {
      /* `reset()` annule la frame en cours ET efface le canvas. Le clearRect
         qui suit couvre le cas où la bibliothèque a perdu ses dimensions après
         un redimensionnement ; le retrait du nœud clôt la question. */
      fire.reset();
      canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    } catch (_) {
      /* canvas déjà démonté — il n'y a plus rien à éteindre */
    }
    canvas.remove();
  };

  /* Le passager qui veut photographier son ticket ne doit pas attendre la fin
     du spectacle : son premier geste l'interrompt. Le canvas ne recevant pas le
     tactile, l'écoute se fait au niveau du document. */
  later(() => document.addEventListener("pointerdown", stop), TAP_SKIP_ARM_MS);

  /* 1. Les confettis — récompense immédiate du dernier coup d'ongle : une
     gerbe centrale, puis deux canons latéraux qui se croisent au-dessus du
     ticket. */
  fire({
    particleCount: 90,
    spread: 80,
    startVelocity: 48,
    origin: { x: 0.5, y: 0.62 },
    colors: palette,
  });
  later(() => {
    fire({
      particleCount: 55,
      angle: 58,
      spread: 62,
      origin: { x: 0, y: 0.75 },
      colors: palette,
    });
    fire({
      particleCount: 55,
      angle: 122,
      spread: 62,
      origin: { x: 1, y: 0.75 },
      colors: palette,
    });
  }, 200);

  /* 2. Le feu d'artifice — des obus qui éclatent en cercle (`spread: 360`) dans
     la moitié haute de l'écran, là où le ticket ne porte encore aucun texte. */
  later(() => {
    const end = Date.now() + FIREWORKS_DURATION_MS;

    const shell = () => {
      // Les deux tirs partent du MÊME point : c'est le décalage de vitesse
      // entre le cœur et la couronne qui donne l'éclat d'une vraie fusée,
      // là où un tir unique ne fait qu'un rond de points.
      const origin = { x: rand(0.15, 0.85), y: rand(0.12, 0.42) };
      fire({
        particleCount: 36,
        spread: 360,
        startVelocity: 26,
        ticks: 70,
        gravity: 0.85,
        decay: 0.91,
        scalar: 0.85,
        origin,
        colors: palette,
      });
      fire({
        particleCount: 16,
        spread: 360,
        startVelocity: 13,
        ticks: 95,
        gravity: 0.55,
        decay: 0.94,
        scalar: 1.2,
        origin,
        colors: palette,
      });
    };

    shell();
    shells = setInterval(() => {
      if (Date.now() >= end) {
        clearInterval(shells);
        shells = null;
        return;
      }
      shell();
    }, SHELL_INTERVAL_MS);
  }, FIREWORKS_DELAY_MS);

  // Le rideau tombe seul : passé ce délai, plus rien à l'écran à photographier.
  later(stop, FX_TOTAL_MS);

  return stop;
}
